import { CloudUpload } from "@mui/icons-material";
import { useEffect, useState } from "react";
import DropZone from "react-dropzone";

import Helpers from "../../commons/helpers";
import useStrings from "../../hooks/useStrings";
import Box from "../Box";
import PreviewFileDialog from "./PreviewFileDialog";
import PreviewImageItem from "./PreviewImageItem";
import { IFileItem, IHiddenActionImageUploader } from ".";
import { dropZoneContainer } from "./styles";
import { IAssetInfo, UploadFileResponse } from "../../commons/interfaces";
import { FileAccessMode, Mode } from "../../constants/enums";
import AssetService from "../../services/asset/asset.service";
import Typography from "../Typography";

interface IFileDropZone {
    mode: Mode;
    multiple?: boolean;
    disabled?: boolean;
    title?: string;
    folderPath: string;
    optimize?: boolean;
    fileList: IFileItem[];
    accessMode?: FileAccessMode;
    fileListContainerRef: React.MutableRefObject<any>;
    onDropFiles?: (newFiles: File[]) => void;
    handleClickSingleChangeFile: (renderKeyId: string) => void;
    handleUploadDone: (renderKeyId: string, res: UploadFileResponse) => void;
    handleRemove: (renderKeyId: string) => void;
    hiddenAction?: IHiddenActionImageUploader;
}

const FileDropZone: React.FC<IFileDropZone> = ({
    fileListContainerRef,
    fileList,
    handleClickSingleChangeFile,
    handleUploadDone,
    handleRemove,
    hiddenAction,
    ...props
}: IFileDropZone) => {
    const strings = useStrings();
    const multiple = !Helpers.isNullOrEmpty(props.multiple) ? props.multiple : true;

    const noClick = props.disabled === true || props.mode === Mode.View ? true : false;

    const onDropFiles = (newFiles: File[]) => {
        if (props.mode === Mode.View) return;
        props.onDropFiles && props.onDropFiles(newFiles);
    };

    const [selectedFile, setSelectedFile] = useState<IAssetInfo>({} as IAssetInfo);
    const [visibled, setVisibled] = useState<boolean>(false);

    const onView = (file: IAssetInfo) => {
        setSelectedFile(file);
        setVisibled(true);
    };
    const onClose = () => {
        setSelectedFile({} as IAssetInfo);
        setVisibled(false);
    };

    const [assetInfos, setAssetInfos] = useState<IAssetInfo[]>([]);

    useEffect(() => {
        const getAssetInfos = async () => {
            const result = await AssetService.getMultiAssetInfo({
                ids: fileList.filter((el) => !Helpers.isNullOrEmpty(el.fileId)).map((el) => el.fileId),
            });
            setAssetInfos(result);
        };
        getAssetInfos();
    }, [fileList, fileList.length]);

    return (
        <>
            <DropZone
                multiple={multiple}
                noClick={noClick}
                disabled={props.disabled}
                onDrop={(acceptedFiles) => {
                    onDropFiles(acceptedFiles);
                }}
                accept={{ "image/*": [] }}
            >
                {({ getRootProps, getInputProps }) => (
                    <Box
                        {...getRootProps()}
                        sx={{
                            maxHeight: "358px",
                            overflowY: "scroll",
                        }}
                    >
                        {fileList.length === 0 ? (
                            <Box sx={dropZoneContainer}>
                                <input {...getInputProps()} type="file" />
                                <CloudUpload style={{ width: "100px", height: "100px" }} />
                                {props.title || strings.Common.DRAG_AND_DROP_FILES_HERE_OR_CLICK}
                            </Box>
                        ) : (
                            <Box
                                ref={fileListContainerRef}
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: {
                                        sx: "repeat(1, 1fr)",
                                        sm: "repeat(2, 1fr)",
                                        md: "repeat(3, 1fr)",
                                        lg: "repeat(4, 1fr)",
                                    },
                                    gap: "1rem",
                                    mt: 1,
                                    padding: "1rem 2rem",
                                    backgroundColor: "#f0f0f0",
                                }}
                            >
                                {fileList.map((file, index) => {
                                    if (file.fileId === "0" && !Helpers.isNullOrEmpty(file.fileUrl)) {
                                        return (
                                            <PreviewImageItem
                                                mode={props.mode}
                                                folderPath={props.folderPath}
                                                key={file.renderKeyId}
                                                index={index}
                                                assetInfo={
                                                    {
                                                        isImage: true,
                                                        id: file.fileId,
                                                        accessUrl: file.fileUrl,
                                                    } as IAssetInfo
                                                }
                                                accessMode={props.accessMode}
                                                optimize={props.optimize}
                                                fileId={file.fileId}
                                                onReplace={() => handleClickSingleChangeFile(file.renderKeyId)}
                                                onRemove={() => handleRemove(file.renderKeyId)}
                                                onView={() =>
                                                    onView({
                                                        isImage: true,
                                                        id: file.fileId,
                                                        accessUrl: file.fileUrl,
                                                    } as IAssetInfo)
                                                }
                                                hiddenAction={hiddenAction}
                                            />
                                        );
                                    }

                                    if (file.fileId) {
                                        return (
                                            <PreviewImageItem
                                                mode={props.mode}
                                                folderPath={props.folderPath}
                                                key={file.renderKeyId}
                                                index={index}
                                                assetInfo={assetInfos.find((el) => el.id === file.fileId)}
                                                accessMode={props.accessMode}
                                                optimize={props.optimize}
                                                fileId={file.fileId}
                                                onReplace={() => handleClickSingleChangeFile(file.renderKeyId)}
                                                onRemove={() => handleRemove(file.renderKeyId)}
                                                onView={() => onView(assetInfos.find((el) => el.id === file.fileId))}
                                                hiddenAction={hiddenAction}
                                            />
                                        );
                                    }

                                    if (file.fileBinary) {
                                        return (
                                            <PreviewImageItem
                                                mode={props.mode}
                                                folderPath={props.folderPath}
                                                key={file.renderKeyId}
                                                index={index}
                                                assetInfo={assetInfos.find((el) => el.id === file.fileId)}
                                                accessMode={props.accessMode}
                                                optimize={props.optimize}
                                                startUpload={file.startUpload}
                                                fileBinary={file.fileBinary}
                                                onUploadDone={(res) => handleUploadDone(file.renderKeyId, res)}
                                                onReplace={() => handleClickSingleChangeFile(file.renderKeyId)}
                                                onRemove={() => handleRemove(file.renderKeyId)}
                                                onView={() => onView(assetInfos.find((el) => el.id === file.fileId))}
                                                hiddenAction={hiddenAction}
                                            />
                                        );
                                    } else {
                                        return null;
                                    }
                                })}
                            </Box>
                        )}
                    </Box>
                )}
            </DropZone>
            {
                <PreviewFileDialog
                    assetInfo={selectedFile}
                    //  fileId={selectedFile.fileId}
                    visibled={visibled}
                    onClose={onClose}
                />
            }
        </>
    );
};

export default FileDropZone;
